import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, Github, ExternalLink, BarChart3 } from 'lucide-react';
import { getProjectBySlug } from '../data/projectsData';
import ReactMarkdown from 'react-markdown';

const ProjectDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const project = getProjectBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!project) {
    return (
      <div className="min-h-screen bg-white dark:bg-black text-black dark:text-white py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-light mb-6">Project Not Found</h1>
          <Button onClick={() => navigate('/projects')} variant="outline" className="gap-2">
            <ArrowLeft size={16} />
            Back to Projects
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-black text-black dark:text-white py-20 px-6">
      <article className="max-w-5xl mx-auto">
        {/* Back Button */}
        <Button 
          onClick={() => navigate('/projects')} 
          variant="ghost" 
          className="gap-2 mb-8 -ml-4"
        >
          <ArrowLeft size={16} />
          Back to Projects
        </Button>

        {/* Header */}
        <header className="mb-12">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <Badge variant="outline" className="text-sm">{project.category}</Badge>
            {project.stats && Object.entries(project.stats).map(([key, value], idx) => (
              <Badge key={idx} variant="secondary" className="text-xs">
                {value}
              </Badge>
            ))}
          </div>
          
          <h1 className="text-4xl md:text-5xl font-light mb-4 leading-tight">
            {project.title}
          </h1>
          
          <p className="text-xl font-light text-gray-600 dark:text-gray-400 mb-4">
            {project.subtitle}
          </p>

          <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
            {project.description}
          </p>

          <div className="flex flex-wrap items-center gap-4 mt-8">
            {(project.liveUrl || project.githubUrl) && (
              <a
                href={project.liveUrl || project.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="default" className="gap-2">
                  {project.liveUrl ? <ExternalLink size={18} /> : <Github size={18} />}
                  {project.liveUrl ? 'View Project' : 'View on GitHub'}
                </Button>
              </a>
            )}
          </div>
        </header>

        <Separator className="mb-12" />

        {/* Featured Image */}
        <div className="relative h-96 mb-12 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-800">
          <img 
            src={project.image} 
            alt={project.title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Technologies */}
        <div className="mb-12">
          <h2 className="text-2xl font-light mb-6">Technologies Used</h2>
          <div className="flex flex-wrap gap-3">
            {project.technologies.map((tech, idx) => (
              <Badge key={idx} variant="outline" className="px-4 py-2 text-sm">
                {tech}
              </Badge>
            ))}
          </div>
        </div>

        <Separator className="mb-12" />

        {/* Project Details */}
        <div className="prose prose-lg dark:prose-invert max-w-none">
          <ReactMarkdown
            components={{
              h2: ({ children }) => (
                <h2 className="text-3xl font-light mt-12 mb-6">{children}</h2>
              ),
              h3: ({ children }) => (
                <h3 className="text-2xl font-light mt-8 mb-4">{children}</h3>
              ),
              p: ({ children }) => (
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">{children}</p>
              ),
              ul: ({ children }) => (
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-700 dark:text-gray-300">{children}</ul>
              ),
              ol: ({ children }) => (
                <ol className="list-decimal list-inside space-y-2 mb-6 text-gray-700 dark:text-gray-300">{children}</ol>
              ),
              strong: ({ children }) => (
                <strong className="font-medium text-black dark:text-white">{children}</strong>
              ),
              li: ({ children }) => (
                <li className="ml-4">{children}</li>
              ),
            }}
          >
            {project.fullDescription}
          </ReactMarkdown>
        </div>

        <Separator className="my-12" />

        {/* Footer */}
        <footer className="flex items-center justify-between">
          <Button onClick={() => navigate('/projects')} variant="outline" className="gap-2">
            <ArrowLeft size={16} />
            Back to Projects
          </Button>
          {(project.liveUrl || project.githubUrl) && (
            <a
              href={project.liveUrl || project.githubUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="outline" className="gap-2">
                {project.liveUrl ? <ExternalLink size={16} /> : <Github size={16} />}
                {project.liveUrl ? 'View Project' : 'View Source Code'}
              </Button>
            </a>
          )}
        </footer>
      </article>
    </div>
  );
};

export default ProjectDetailPage;